/**
 * Deployment Alerts Settings (Superadmin)
 *
 * Configure the Slack channel used for deployment changelog notifications.
 * Uses the internal org's Slack connection and a knob for the channel ID.
 *
 * @module
 * @see apps/api/src/superadmin/routes.ts - Backend routes
 * @see functions/deployment-notifier/src/release.ts - Changelog dispatch logic
 */

import React from 'react';
import { Flex, Box, Heading, Text, Card, CardHeader, CardContent, Button, Select, LoadingOrbit, type SelectOption } from '@chatsdet/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, PaperPlaneTilt } from '@phosphor-icons/react';
import { API_URL } from '../../../config';

// ============================================================================
// Types
// ============================================================================

interface DeploymentAlertsSettings {
  channelId: string | null;
  channelName: string | null;
  connected: boolean;
  internalOrgId: string | null;
  internalOrgName: string | null;
}

interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

interface SettingsResponse {
  ok: boolean;
  data: DeploymentAlertsSettings;
}

interface ChannelsResponse {
  ok: boolean;
  data: SlackChannel[];
  error?: string;
}

interface TestResponse {
  ok: boolean;
  message?: string;
  error?: string;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchSettings(): Promise<SettingsResponse> {
  const res = await fetch(`${API_URL}/superadmin/deployment-alerts/settings`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch deployment alerts settings');
  return res.json();
}

async function fetchChannels(): Promise<ChannelsResponse> {
  const res = await fetch(`${API_URL}/superadmin/deployment-alerts/channels`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to fetch channels');
  }
  return res.json();
}

async function updateSettings(settings: {
  channelId?: string;
  channelName?: string;
}): Promise<SettingsResponse> {
  const res = await fetch(`${API_URL}/superadmin/deployment-alerts/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error('Failed to update settings');
  return res.json();
}

async function sendTestNotification(): Promise<TestResponse> {
  const res = await fetch(`${API_URL}/superadmin/deployment-alerts/test`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error ?? 'Failed to send test notification');
  }
  return res.json();
}

// ============================================================================
// Component
// ============================================================================

export function SuperadminDeploymentAlerts() {
  const queryClient = useQueryClient();

  // Queries
  const settingsQuery = useQuery({
    queryKey: ['superadmin', 'deployment-alerts', 'settings'],
    queryFn: fetchSettings,
  });

  const channelsQuery = useQuery({
    queryKey: ['superadmin', 'deployment-alerts', 'channels'],
    queryFn: fetchChannels,
    enabled: !!settingsQuery.data?.data.connected,
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'deployment-alerts', 'settings'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: sendTestNotification,
  });

  // Local state
  const [selectedChannelId, setSelectedChannelId] = React.useState<string | null>(null);

  // Sync local state with fetched settings
  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setSelectedChannelId(settingsQuery.data.data.channelId);
    }
  }, [settingsQuery.data]);

  const settings = settingsQuery.data?.data;
  const channels = channelsQuery.data?.data ?? [];

  const handleChannelChange = async (channelId: string) => {
    setSelectedChannelId(channelId);
    const channel = channels.find((ch) => ch.id === channelId);
    await updateMutation.mutateAsync({
      channelId,
      channelName: channel?.name,
    });
  };

  const handleTestNotification = () => {
    testMutation.mutate();
  };

  if (settingsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-20">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card padding="lg">
        <CardContent>
          <Text className="text-destructive">
            {settingsQuery.error instanceof Error
              ? settingsQuery.error.message
              : 'Failed to load deployment alerts settings'}
          </Text>
        </CardContent>
      </Card>
    );
  }

  const isConfigured = settings?.connected && settings?.channelId;

  return (
    <Box className="space-y-6">
      {/* Status Card */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Deployment Changelog Status</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Slack channel for release changelogs and deploy notifications.
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="xs">
            <Flex align="center" gap="sm">
              {settings?.internalOrgId ? (
                <Check size={14} className="text-green-600" />
              ) : (
                <X size={14} className="text-muted-foreground" />
              )}
              <Text size="sm">
                Internal Org: {settings?.internalOrgName ?? settings?.internalOrgId ?? 'Not configured (set INTERNAL_ORG_ID)'}
              </Text>
            </Flex>
            <Flex align="center" gap="sm">
              {settings?.connected ? (
                <Check size={14} className="text-green-600" />
              ) : (
                <X size={14} className="text-muted-foreground" />
              )}
              <Text size="sm">
                Slack: {settings?.connected ? 'Connected' : 'Not connected'}
              </Text>
            </Flex>
            <Flex align="center" gap="sm">
              {settings?.channelId ? (
                <Check size={14} className="text-green-600" />
              ) : (
                <X size={14} className="text-muted-foreground" />
              )}
              <Text size="sm">
                Channel: {settings?.channelName ? `#${settings.channelName}` : settings?.channelId ?? 'Not set'}
              </Text>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      {/* Channel Configuration Card */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Slack Channel</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Select which channel to send deployment changelogs to.
          </Text>
        </CardHeader>
        <CardContent>
          {!settings?.internalOrgId ? (
            <Text size="sm" className="text-amber-600">
              Set the INTERNAL_ORG_ID environment variable to enable deployment alerts.
            </Text>
          ) : !settings?.connected ? (
            <Text size="sm" className="text-amber-600">
              The internal organization does not have a Slack connection. Connect Slack from the org settings first.
            </Text>
          ) : (
            <Box className="max-w-md">
              <Text size="xs" className="text-muted-foreground mb-1.5 block font-medium">
                Changelog Channel
              </Text>
              {channelsQuery.isLoading ? (
                <LoadingOrbit size="sm" />
              ) : channelsQuery.isError ? (
                <Text size="sm" className="text-destructive">
                  {channelsQuery.error instanceof Error
                    ? channelsQuery.error.message
                    : 'Failed to load channels'}
                </Text>
              ) : channels.length === 0 ? (
                <Text size="sm" className="text-muted-foreground">
                  No channels available. Make sure the Slack bot is added to channels.
                </Text>
              ) : (
                <Select
                  items={channels.map((ch) => ({
                    id: ch.id,
                    label: `#${ch.name}`,
                  })) satisfies SelectOption[]}
                  selectedKey={selectedChannelId ?? undefined}
                  onSelectionChange={(value) => handleChannelChange(value as string)}
                  isDisabled={updateMutation.isPending}
                  placeholder="Select a channel..."
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* About Card */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>About</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            What gets posted to this channel.
          </Text>
        </CardHeader>
        <CardContent>
          <Text size="sm" className="text-muted-foreground">
            Release changelogs are automatically posted to this channel whenever a production
            deployment completes. The changelog is generated from the release-please PR and
            includes version numbers and commit summaries. This channel was previously
            hardcoded and is now configurable via this settings page.
          </Text>
        </CardContent>
      </Card>

      {/* Test Card */}
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Test Notification</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Send a test message to verify the Slack integration is working.
              </Text>
            </div>
            <Button
              variant="outline"
              onPress={handleTestNotification}
              isDisabled={testMutation.isPending || !isConfigured}
            >
              <PaperPlaneTilt size={16} className="mr-2" />
              {testMutation.isPending ? 'Sending...' : 'Send Test Notification'}
            </Button>
          </Flex>
        </CardHeader>
        <CardContent>
          {testMutation.isSuccess && (
            <Text size="sm" className="text-green-600">
              Test notification sent successfully. Check the configured Slack channel.
            </Text>
          )}
          {testMutation.isError && (
            <Text size="sm" className="text-destructive">
              {testMutation.error instanceof Error
                ? testMutation.error.message
                : 'Failed to send test notification'}
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
